import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import { getBlogByIdApi } from "../../../services/BlogService";

const LANGUAGE_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };
const FLAG_ICONS = { en: "flag-icon-us", ar: "flag-icon-sa", ku: "flag-icon-iq" };

const StatusBadge = ({ status }) => {
  const map = {
    published: "badge-success",
    draft: "badge-warning",
    scheduled: "badge-info",
  };
  return (
    <span className={`badge light ${map[status] || "badge-secondary"}`} style={{ textTransform: "capitalize" }}>
      {status || "draft"}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getBlogByIdApi(id);
        setBlog(res?.data?.data || null);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load blog.");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id]);

  if (loading) {
    return (
      <>
        <PageTitle activeMenu="Blog Detail" motherMenu="Blogs" />
        <div className="card"><div className="card-body text-center py-5 text-muted">Loading blog...</div></div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <PageTitle activeMenu="Blog Detail" motherMenu="Blogs" />
        <div className="alert alert-danger">{error || "Blog not found."}</div>
        <button className="btn btn-primary" onClick={() => navigate("/all-blogs")}>Back to Blogs</button>
      </>
    );
  }

  const langLabel = LANGUAGE_LABELS[blog.language] || blog.language || "English";
  const flagIcon = FLAG_ICONS[blog.language] || "flag-icon-us";

  return (
    <>
      <PageTitle activeMenu="Blog Detail" motherMenu="Blogs" />

      {/* ── Action Bar ── */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        <Link to="/all-blogs" className="btn btn-sm btn-outline-primary">
          <i className="fa fa-arrow-left me-1" /> Back to Blogs
        </Link>
        <Link to={`/edit-blog/${blog.id}`} className="btn btn-sm btn-primary">
          <i className="fa fa-pencil me-1" /> Edit Blog
        </Link>
      </div>

      {/* ── Hero Image ── */}
      {blog.featuredImage && (
        <div className="card mb-4 overflow-hidden" style={{ borderRadius: 12 }}>
          <img
            src={blog.featuredImage}
            alt={blog.title}
            style={{ width: "100%", maxHeight: 420, objectFit: "cover", display: "block" }}
          />
        </div>
      )}

      <div className="row">
        {/* ── Left Sidebar ── */}
        <div className="col-xl-3 col-lg-4">

          {/* Meta card */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Blog Info</h5>
            </div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush">
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Status</span>
                  <StatusBadge status={blog.publishStatus} />
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Category</span>
                  <span className="fw-semibold small" style={{ textTransform: "capitalize" }}>{blog.category || "-"}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Author</span>
                  <span className="fw-semibold small">{blog.authorName || "-"}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Published</span>
                  <span className="fw-semibold small">{formatDate(blog.publishDate || blog.createdAt)}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Reading Time</span>
                  <span className="fw-semibold small">{blog.readingTime || 1} min</span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Featured</span>
                  <span className={`badge light ${blog.isFeatured ? "badge-success" : "badge-secondary"}`}>
                    {blog.isFeatured ? "Yes" : "No"}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Language</span>
                  <span className="fw-semibold small">
                    <i className={`flag-icon ${flagIcon} me-1`} />
                    {langLabel}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center px-3 py-2">
                  <span className="text-muted small">Slug</span>
                  <span className="text-muted small text-truncate ms-2" style={{ maxWidth: 120 }} title={blog.slug}>/{blog.slug}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Tags */}
          {Array.isArray(blog.tags) && blog.tags.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Tags</h5>
              </div>
              <div className="card-body d-flex flex-wrap gap-2">
                {blog.tags.map((tag) => (
                  <span key={tag} className="badge badge-primary light px-3 py-2" style={{ fontSize: 12 }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SEO */}
          {blog.seo && (blog.seo.metaTitle || blog.seo.metaDescription || blog.seo.focusKeyword) && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">SEO</h5>
              </div>
              <div className="card-body">
                {blog.seo.metaTitle && (
                  <div className="mb-2">
                    <div className="text-muted small mb-1">Meta Title</div>
                    <div className="small fw-semibold">{blog.seo.metaTitle}</div>
                  </div>
                )}
                {blog.seo.focusKeyword && (
                  <div className="mb-2">
                    <div className="text-muted small mb-1">Focus Keyword</div>
                    <span className="badge badge-info light">{blog.seo.focusKeyword}</span>
                  </div>
                )}
                {blog.seo.metaDescription && (
                  <div>
                    <div className="text-muted small mb-1">Meta Description</div>
                    <div className="small text-muted">{blog.seo.metaDescription}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Main Content ── */}
        <div className="col-xl-9 col-lg-8">
          <div className="card mb-4">
            <div className="card-body">
              {/* Title + Excerpt */}
              <h2 className="mb-2" style={{ lineHeight: 1.35 }}>{blog.title}</h2>
              <p className="text-muted" style={{ fontSize: 15, lineHeight: 1.7, borderLeft: "3px solid #6c757d", paddingLeft: 12 }}>
                {blog.excerpt}
              </p>

              <hr className="my-4" />

              {/* Table of Contents */}
              {Array.isArray(blog.tableOfContents) && blog.tableOfContents.length > 0 && (
                <div className="card mb-4" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
                  <div className="card-body py-3">
                    <h6 className="mb-3 text-primary">Table of Contents</h6>
                    <ol className="mb-0 ps-3">
                      {blog.tableOfContents.map((item, i) => (
                        <li
                          key={i}
                          style={{ marginLeft: (item.level - 1) * 16, fontSize: 14, marginBottom: 4 }}
                        >
                          <a href={`#${item.id}`} className="text-decoration-none text-dark">
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              )}

              {/* Blog Content */}
              <div
                className="blog-content"
                style={{ lineHeight: 1.85, fontSize: 15 }}
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>
          </div>

          {/* Related Blogs */}
          {Array.isArray(blog.relatedBlogs) && blog.relatedBlogs.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Related Blogs</h5>
              </div>
              <div className="card-body d-flex flex-wrap gap-2">
                {blog.relatedBlogs.map((relId) => (
                  <Link
                    key={relId}
                    to={`/blog-detail/${relId}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    <i className="fa fa-link me-1" />
                    View Blog
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
